import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { Company } from "@repo/app-types/entities/company";
import { CompanyMode } from "@repo/app-types/entities/company";
import {
  isAlpharunEmail,
  isCompanyBlocked,
} from "@repo/app-types/helpers/utilities";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import type { RootState } from "../../app/store.js";

export interface CompanyState {
  company: Company | null;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}
const initialState: CompanyState = {
  company: null,
  status: "idle",
  error: null,
};

// Async Action
interface PostCompanyDataArgs {
  companyName: string;
  shouldEnableDomainLogin: boolean;
}

export const postCompanyData = createAsyncThunk(
  "company/post",
  async (args: PostCompanyDataArgs, thunkAPI) => {
    const { companyName, shouldEnableDomainLogin } = args;
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.post("/company", {
        name: companyName,
        should_enable_domain_login: shouldEnableDomainLogin,
      });

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

// Slice
const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    setCompany: (state, action: PayloadAction<Company>) => {
      return {
        ...state,
        company: action.payload,
        status: "succeeded",
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postCompanyData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postCompanyData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.company = action.payload;
      })
      .addCase(postCompanyData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      });
  },
});

export const { setCompany } = companySlice.actions;
export default companySlice.reducer;

export const selectCompany = (state: RootState) => state.company.company;
export const selectCompanyLoadingState = (state: RootState) =>
  state.company.status;
export const selectIsCompanyBlocked = (state: RootState) => {
  const company = state.company.company;

  // Dont block if we're still loading the company
  if (!company) {
    return false;
  }

  return isCompanyBlocked(company);
};

export const selectIsRecruitingModeCompany = (state: RootState) => {
  return state.company.company?.settings.mode === CompanyMode.RECRUITING;
};

export const selectIsFountainCompany = (state: RootState) => {
  return state.company.company?.billing_current_plan === "fountain-1";
};

export const selectIsUsingSSO = (state: RootState) => {
  return state.company.company?.settings.identity_provider_id !== null;
};

export const selectIsCheatingDetectionEnabled = (state: RootState) => {
  // For debugging purposes, always show cheating detection for Alpharun teammates.
  if (isAlpharunEmail(state.teammate.teammate?.email)) {
    return true;
  }

  return state.company.company?.settings.is_cheating_detection_enabled;
};
